import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTable,
  CardTableRow,
  ValueAutocompleteField,
  useTheme,
} from '@forgeglobal/design-framework'
import { ConditionRowBuilder } from './ConditionRowBuilder'
import { FieldMap, fieldMapFromQuery, parseTotalCondition, prettifyName } from 'util/SegmentUtils'
import { Form, FormRenderProps, useField } from 'react-final-form'
import { FormApi } from 'final-form'
import { Routes } from 'util/Routes'
import { createRandomString } from 'util/StringUtils'
import {
  useAccountTableSchemaQuery,
  useFilterAccountsCountOnlyQuery,
  useQueriedAccountsQuery,
} from 'graphql/bindings/AdminBindings'
import React, { useEffect, useMemo, useState } from 'react'
import omit from 'lodash/omit'
import toPairs from 'lodash/toPairs'
import values from 'lodash/values'

type ConditionState = { [key: string]: string }

const columnDefs = [
  {
    name: 'name',
    label: 'Name',
    minWidth: 100,
    isLabelHidden: false,
  },
  {
    name: 'type',
    label: 'Type',
    minWidth: 100,
    isLabelHidden: false,
  },
  {
    name: 'email',
    label: 'Email',
    minWidth: 100,
    isLabelHidden: false,
  },
  {
    name: 'last_order_closed_date',
    label: 'Last transacted',
    minWidth: 100,
    isLabelHidden: false,
  },
  {
    name: 'on_behalf_of',
    label: 'On Behalf Of',
    minWidth: 150,
  },
]

export const SegmentBuilderField: React.FC<{
  name: string
  forcedCondition?: string
  onPosCountChange?: (count: number) => void
  onTotalCountChange?: (count: number) => void
}> = ({ name, forcedCondition, onPosCountChange, onTotalCountChange }) => {
  const { input } = useField<string>(name)
  return (
    <SegmentBuilder
      onChange={input.onChange}
      value={input.value}
      forcedCondition={forcedCondition}
      onPosCountChange={onPosCountChange}
      onTotalCountChange={onTotalCountChange}
    />
  )
}

type ConditionType = 'include' | 'exclude'
interface ConditionCardProps {
  type: ConditionType
  fieldMap: FieldMap
  forcedCondition?: string
  countAndCondition?: string
  onConditionChange: (condition: string) => void
  conditionState: ConditionState
  setConditionState: React.Dispatch<React.SetStateAction<ConditionState>>
  onCountChange?: (count: number) => void
}

const conditionsFromCriteria = (criteria: string[], forcedCondition?: string) => {
  const filteredCriteria = criteria.filter(criterion => criterion !== forcedCondition)
  return filteredCriteria
    ? filteredCriteria.reduce((acc, val) => {
        return { ...acc, [createRandomString()]: val }
      }, {} as ConditionState)
    : {}
}

interface NewConditionFormShape {
  condition?: string
  type?: ConditionType
}

const NewConditionForm: React.FC<FormRenderProps<NewConditionFormShape> & { fieldMap: FieldMap }> = ({
  handleSubmit,
  fieldMap,
}) => {
  const { palette } = useTheme()
  return (
    <form onSubmit={handleSubmit}>
      <Box
        display="flex"
        p={2}
        mb={1}
        border={`1px solid ${palette.divider}`}
        alignItems="center"
        justifyContent="space-between">
        <Box display="flex" alignItems="center" justifyContent="space-between" width="100%" flexShrink={1}>
          <Box mr={1} width={160}>
            <ValueAutocompleteField
              label="Strategy"
              name="type"
              options={[
                { label: 'Include', value: 'include' },
                { label: 'Exclude', value: 'exclude' },
              ]}
            />
          </Box>
          <Box width="100%" flexGrow={1} flexShrink={2}>
            <ConditionRowField name="condition" fieldMap={fieldMap} />
          </Box>
        </Box>
        <Box ml={1} display="flex" alignItems="center">
          <Button color="primary" type="submit">
            Add
          </Button>
        </Box>
      </Box>
    </form>
  )
}

const ConditionRowField: React.FC<{ name: string; fieldMap: FieldMap }> = ({ name, fieldMap }) => {
  const { input } = useField(name)
  return <ConditionRowBuilder fields={fieldMap} onConditionChange={input.onChange} value={input.value} />
}

const ConditionCard: React.FC<ConditionCardProps> = ({
  type,
  fieldMap,
  onConditionChange,
  forcedCondition,
  countAndCondition,
  conditionState,
  setConditionState,
  onCountChange,
}) => {
  const conditions = forcedCondition ? [forcedCondition, ...values(conditionState)] : values(conditionState)
  const stringCondition = conditions.map(e => `(${e})`).join(' or ')

  useEffect(() => {
    onConditionChange(stringCondition)
  }, [stringCondition, onConditionChange])

  const countCondition = countAndCondition ? `(${countAndCondition}) and (${stringCondition})` : stringCondition
  const { data: queryCountData } = useFilterAccountsCountOnlyQuery({
    variables: { query: countCondition },
  })

  const count = queryCountData?.filterAccounts?.count ?? 0

  useEffect(() => {
    onCountChange && onCountChange(count)
  }, [onCountChange, count])
  const conditionPairs = toPairs(conditionState)

  return (
    <Card>
      <CardHeader title={type === 'include' ? 'Include' : 'Exclude'} />
      <CardContent>
        {type === 'include' && conditionPairs.length === 0 && 'By default, all accounts are included'}
        {conditionPairs.map(val => {
          const handleChange = (cond: string) => {
            setConditionState(currentState => {
              return { ...currentState, [val[0]]: cond }
            })
          }
          const handleDelete = () => {
            setConditionState(currentState => {
              return omit(currentState, [val[0]])
            })
          }
          return (
            <ConditionRowBuilder
              fields={fieldMap}
              onConditionChange={handleChange}
              key={val[0]}
              value={val[1]}
              onDelete={handleDelete}
            />
          )
        })}
        {forcedCondition && "* We're excluding accounts in restricted countries or have unsubscribed"}
      </CardContent>
    </Card>
  )
}

export const SegmentBuilder: React.FC<{
  onChange?: (value: string) => void
  value?: string
  forcedCondition?: string
  onPosCountChange?: (count: number) => void
  onTotalCountChange?: (count: number) => void
}> = ({ onChange, value, forcedCondition, onPosCountChange, onTotalCountChange }) => {
  const { data } = useAccountTableSchemaQuery()
  const criteria = value && parseTotalCondition(value)
  const includeCriteria = criteria ? criteria.include : []
  const excludeCriteria = criteria ? criteria.exclude : []
  const [positiveCondition, setPositiveCondition] = useState('')
  const [negativeCondition, setNegativeCondition] = useState('')
  const [posConditionState, setPosConditions] = useState<ConditionState>(conditionsFromCriteria(includeCriteria))
  const [negConditionState, setNegConditions] = useState<ConditionState>(
    conditionsFromCriteria(excludeCriteria, forcedCondition)
  )
  const [renderCounter, setRenderCounter] = useState(0)

  const totalCondition = useMemo(
    () =>
      (positiveCondition.length ? `(${positiveCondition})` : '') +
      (positiveCondition.length && negativeCondition.length ? ' and ' : '') +
      (negativeCondition.length ? `(not (${negativeCondition}))` : ''),
    [positiveCondition, negativeCondition]
  )

  useEffect(() => {
    onChange && onChange(totalCondition)
  }, [totalCondition, onChange])

  const baseVariables = { query: totalCondition, limit: 10 }
  const { data: queryPreviewData, refetch: refetchQuery } = useQueriedAccountsQuery({
    variables: { ...baseVariables, offset: 0 },
  })

  const fieldsLookup = fieldMapFromQuery(data)
  const queryResults = queryPreviewData?.filterAccounts.data ?? []
  const count = queryPreviewData?.filterAccounts.count ?? 0
  const offset = queryPreviewData?.filterAccounts.offset ?? 0
  const limit = queryPreviewData?.filterAccounts.limit ?? 0
  useEffect(() => {
    onTotalCountChange && onTotalCountChange(count)
  }, [onTotalCountChange, count])
  const handlePageChange = (newOffset: number) => {
    refetchQuery({ ...baseVariables, offset: newOffset })
  }

  const addNewCondition = (values: NewConditionFormShape, form: FormApi<NewConditionFormShape>) => {
    const cond = values.condition
    if (cond) {
      if (values.type === 'include') {
        setPosConditions(currentState => ({ ...currentState, [createRandomString()]: cond }))
      } else {
        setNegConditions(currentState => ({ ...currentState, [createRandomString()]: cond }))
      }
      setRenderCounter(count => count + 1)
    }
  }

  return (
    <Box pl={4} pr={4} mt={2}>
      <Form<NewConditionFormShape>
        key={renderCounter}
        render={props => <NewConditionForm fieldMap={fieldsLookup} {...props} />}
        initialValues={{ type: 'include' }}
        onSubmit={addNewCondition}
      />
      <ConditionCard
        type="include"
        onCountChange={onPosCountChange}
        fieldMap={fieldsLookup}
        onConditionChange={setPositiveCondition}
        conditionState={posConditionState}
        setConditionState={setPosConditions}
      />
      <ConditionCard
        type="exclude"
        fieldMap={fieldsLookup}
        forcedCondition={forcedCondition}
        onConditionChange={setNegativeCondition}
        countAndCondition={positiveCondition}
        conditionState={negConditionState}
        setConditionState={setNegConditions}
      />
      <Box mt={2}>
        <CardTable
          header="Preview"
          colDefs={columnDefs}
          onSort={() => void 0}
          count={count}
          offset={offset}
          pageSize={limit}
          onPageChange={handlePageChange}>
          {queryResults.map(value => {
            const parents = value.parents
            const intermediaryOrPocParents = parents
              .filter(parent => {
                return parent.id === parent?.parent_account?.intermediary?.id || parent.is_point_of_contact
              })
              .map(parent => parent.parent_account.name)
            const onBehalfOf = intermediaryOrPocParents.length !== 0 ? intermediaryOrPocParents.join(', ') : ''
            return (
              <CardTableRow
                key={value.id}
                id={value.id}
                link={Routes.account(value.id)}
                columns={{
                  name: value.name,
                  email: value.email,
                  type: prettifyName(value.type),
                  last_order_closed_date: value.last_order_closed_date,
                  on_behalf_of: onBehalfOf,
                }}
              />
            )
          })}
        </CardTable>
      </Box>
    </Box>
  )
}
